'use client';

import { useEffect, useState } from 'react';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Header from '@/components/common/Header';
import PresenterMode from './components/PresenterMode';

interface SlideProps {
  slideId?: string;
  children: React.ReactNode;
  className?: string;
}

const Slide = ({ slideId = '', children, className = '' }: SlideProps) =>
<section
  id={slideId}
  className={`relative min-h-screen flex flex-col justify-center overflow-hidden ${className}`}>
  
    {children}
  </section>;


const GlowOrb = ({ className }: {className: string;}) =>
<div className={`absolute rounded-full blur-3xl pointer-events-none ${className}`} />;


const ScanLine = () =>
<div className="absolute inset-0 pointer-events-none overflow-hidden">
    <div
    className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent"
    style={{ animation: 'scanDown 6s linear infinite' }} />
  
  </div>;


const GridBg = () =>
<div
  className="absolute inset-0 pointer-events-none opacity-[0.04]"
  style={{
    backgroundImage: `
        linear-gradient(rgba(0,212,255,1) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0,212,255,1) 1px, transparent 1px)
      `,
    backgroundSize: '60px 60px'
  }} />;



const StatPill = ({ value, label, color = 'accent' }: {value: string;label: string;color?: string;}) =>
<div className="flex flex-col items-center px-6 py-4 bg-card/60 backdrop-blur-sm border border-border rounded-xl hover:border-accent/50 transition-all duration-300 group">
    <span
    className={`font-headline text-3xl lg:text-4xl font-bold mb-1 ${
    color === 'orange' ? 'text-mission-orange' : color === 'green' ? 'text-aerospace-green' : 'text-accent'}`
    }>
    
      {value}
    </span>
    <span className="font-cta text-xs text-technical-gray tracking-wider text-center">{label}</span>
  </div>;


const Badge = ({ text, color = 'accent' }: {text: string;color?: string;}) =>
<span
  className={`inline-flex items-center px-3 py-1 rounded-full border font-cta text-xs tracking-wider ${
  color === 'orange' ? 'bg-mission-orange/10 border-mission-orange/30 text-mission-orange' :
  color === 'green' ? 'bg-aerospace-green/10 border-aerospace-green/30 text-aerospace-green' : 'bg-accent/10 border-accent/30 text-accent'}`
  }>
  
    {text}
  </span>;


const SlideNumber = ({ current, total }: {current: number;total: number;}) =>
<div className="fixed bottom-8 right-8 z-50 flex items-center space-x-2 bg-card/80 backdrop-blur-sm border border-border rounded-full px-4 py-2">
    <span className="font-mono text-accent text-sm font-bold">{String(current).padStart(2, '0')}</span>
    <span className="text-border">/</span>
    <span className="font-mono text-technical-gray text-sm">{String(total).padStart(2, '0')}</span>
  </div>;


const NavDots = ({ slides, active }: {slides: string[];active: string;}) =>
<div className="fixed right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col space-y-3">
    {slides.map((id) =>
  <a
    key={id}
    href={`#${id}`}
    className={`w-2 h-2 rounded-full transition-all duration-300 ${
    active === id ? 'bg-accent scale-150 shadow-glow' : 'bg-border hover:bg-accent/50'}`
    }
    title={id} />

  )}
  </div>;


export default function CompanyDeckPage() {
  const [activeSlide, setActiveSlide] = useState('cover');
  const [slideIndex, setSlideIndex] = useState(1);
  const [presenterMode, setPresenterMode] = useState(false);
  const slideIds = ['cover', 'overview', 'sectors', 'programs', 'partnership', 'flowchart', 'capabilities', 'certifications', 'stats', 'contact'];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSlide(entry.target.id);
            setSlideIndex(slideIds.indexOf(entry.target.id) + 1);
          }
        });
      },
      { threshold: 0.5 }
    );
    slideIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <main className="bg-background text-foreground">
      {/* Presenter Mode Overlay */}
      {presenterMode &&
      <PresenterMode
        onClose={() => setPresenterMode(false)}
        initialSlideIndex={slideIds.indexOf(activeSlide)} />

      }

      <style>{`
        @keyframes scanDown {
          0% { top: -2px; }
          100% { top: 100%; }
        }
        @keyframes floatY {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes rotateRing {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulseRing {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
        @keyframes dataStream {
          0% { transform: translateY(100%); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100%); opacity: 0; }
        }
        @keyframes certReveal {
          0% { opacity: 0; transform: translateY(32px) scale(0.92); }
          100% { opacity: 1; transform: translateY(0px) scale(1); }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 12px 2px var(--glow-color, rgba(0,212,255,0.25)); }
          50% { box-shadow: 0 0 32px 8px var(--glow-color, rgba(0,212,255,0.55)); }
        }
        @keyframes shimmerLine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes countUp {
          0% { opacity: 0; transform: scale(0.8); }
          100% { opacity: 1; transform: scale(1); }
        }
        .cert-card-reveal { animation: certReveal 0.7s cubic-bezier(0.22,1,0.36,1) both; }
        .cert-glow-green { animation: glowPulse 3s ease-in-out infinite; --glow-color: rgba(0,255,136,0.35); }
        .cert-glow-accent { animation: glowPulse 3s ease-in-out infinite; --glow-color: rgba(0,212,255,0.35); }
        .cert-glow-orange { animation: glowPulse 3s ease-in-out infinite; --glow-color: rgba(255,107,0,0.35); }
        .float-anim { animation: floatY 4s ease-in-out infinite; }
        .rotate-ring { animation: rotateRing 20s linear infinite; }
        .pulse-ring { animation: pulseRing 3s ease-in-out infinite; }
      `}</style>

      <Header />
      <NavDots slides={slideIds} active={activeSlide} />
      <SlideNumber current={slideIndex} total={slideIds.length} />

      {/* ── PRESENTER MODE BUTTON ── */}
      <button
        onClick={() => setPresenterMode(true)}
        className="fixed bottom-8 left-8 z-50 flex items-center space-x-2 bg-[#020c14]/90 backdrop-blur-sm border border-[#00d4ff]/30 rounded-full px-4 py-2 text-[#00d4ff] hover:bg-[#00d4ff]/10 hover:border-[#00d4ff]/60 transition-all duration-300 group"
        title="Enter Presenter Mode">
        
        <Icon name="PresentationChartBarIcon" size={16} className="group-hover:scale-110 transition-transform duration-200" />
        <span className="font-mono text-xs tracking-widest hidden sm:block">PRESENT</span>
      </button>

      {/* ── SLIDE 1: COVER ── */}
      <Slide slideId="cover" className="bg-gradient-to-br from-[#020c14] via-[#030f1a] to-[#020c14]">
        <GridBg />
        <ScanLine />
        <GlowOrb className="top-1/4 left-1/4 w-[500px] h-[500px] bg-accent/8" />
        <GlowOrb className="bottom-1/4 right-1/4 w-[400px] h-[400px] bg-mission-orange/6" />

        {/* Full-bleed background image with dark overlay */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1718992073559-ce31515ad7c4"
            alt="Aerospace fighter jet in flight"
            className="w-full h-full object-cover opacity-10" />
          
          <div className="absolute inset-0 bg-gradient-to-b from-[#020c14]/60 via-transparent to-[#020c14]/80" />
        </div>

        {/* Rotating ring decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none">
          <div className="rotate-ring absolute inset-0 rounded-full border border-accent/10" />
          <div
            className="absolute inset-8 rounded-full border border-mission-orange/8"
            style={{ animation: 'rotateRing 30s linear infinite reverse' }} />
          
          <div className="pulse-ring absolute inset-16 rounded-full border border-accent/15" />
        </div>

        <div className="relative z-10 mx-auto px-6 lg:px-8 pt-24 pb-16 text-center max-w-5xl">
          {/* Logo mark */}
          <div className="flex justify-center mb-8">
            <div className="relative float-anim">
              <img
                src="/assets/images/Screenshot_2026-06-28_at_11.22.51_AM-1782626245471.png"
                alt="Wings Bharath Logo"
                className="w-28 h-28 object-contain drop-shadow-[0_0_30px_rgba(0,212,255,0.4)]" />
              
            </div>
          </div>

          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/30 mb-6">
            <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
            <span className="font-cta text-xs text-accent tracking-widest">COMPANY OVERVIEW DECK · 2026</span>
          </div>

          <h1 className="font-headline text-5xl lg:text-7xl xl:text-8xl font-bold text-foreground mb-4 leading-none tracking-tight">
            WINGS
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent via-[#66fcff] to-mission-orange">
              BHARATH
            </span>
          </h1>

          <p className="font-cta text-sm lg:text-base text-accent/80 tracking-[0.3em] mb-6 uppercase">
            AI INTEGRATED PROJECT MANAGEMENT & MANUFACTURING
          </p>

          <p className="font-body text-lg lg:text-xl text-technical-gray max-w-3xl mx-auto leading-relaxed mb-12">Delivering Hi-Precision Structural components for commercial Aerospace, Defence, Space,  Robotics and other industrial programs.

          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-16">
            <Badge text="AIRBUS APPROVED SUPPLIER" color="green" />
            <Badge text="RAFALE CLASS 2 & 3" color="orange" />
            <Badge text="LCA TEJAS PROGRAM" color="accent" />
            <Badge text="REMOTE WEAPON SYSTEMS" color="green" />
          </div>

          <a
            href="#overview"
            className="inline-flex items-center space-x-2 text-technical-gray hover:text-accent transition-colors duration-300 animate-bounce">
            
            <span className="font-cta text-xs tracking-widest">SCROLL TO EXPLORE</span>
            <Icon name="ChevronDownIcon" size={16} />
          </a>
        </div>
      </Slide>

      {/* ── SLIDE 2: COMPANY OVERVIEW ── */}
      <Slide slideId="overview" className="bg-gradient-to-b from-[#020c14] to-[#030f1a] py-24">
        <GridBg />
        <GlowOrb className="top-0 right-0 w-[600px] h-[400px] bg-accent/5" />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-px bg-accent" />
            <span className="font-cta text-xs text-accent tracking-widest">SLIDE 02 · WHO WE ARE</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-headline text-4xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Precision at the
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent to-mission-orange">
                  Speed of Flight
                </span>
              </h2>
              <p className="font-body text-lg text-technical-gray leading-relaxed mb-8">Wings Bharath is an Strategic Partner with Halamanee Aerospace group of Companies - Airbus-approved, defence-certified precision manufacturer headquartered in India. We manufcature precision structural and sheet metal components, machined assemblies, and complex sub-systems for the world's most demanding Aerospace and Defence programs.

              </p>
              <p className="font-body text-base text-technical-gray leading-relaxed mb-10">
                From Airbus A320 cabin structures to LCA Tejas fuselage assemblies and ISRO satellite frames — every part we deliver is built to mission-critical tolerances with full traceability and approved supplier status.
              </p>

              <div className="flex flex-wrap gap-3">
                <Badge text="Founded in India" color="accent" />
                <Badge text="Global Aerospace Supply Chain" color="green" />
                <Badge text="Defence Certified" color="orange" />
              </div>
            </div>

            {/* Right: image + stats bento */}
            <div className="flex flex-col gap-4">
              {/* Hero image */}
              <div className="relative rounded-2xl overflow-hidden border border-accent/20 group">
                <img
                  src="/assets/images/JOURNAL1_11zon-2_11zon-1783095749753.jpg"
                  alt="Precision CNC machining of aerospace components in a hi-tech manufacturing facility"
                  className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-700" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-[#020c14]/80 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                  <span className="font-cta text-[10px] text-accent tracking-widest">PRECISION MANUFACTURING</span>
                </div>
                <div className="absolute top-3 right-3 px-2 py-1 bg-accent/15 border border-accent/30 rounded-full">
                  <span className="font-cta text-[9px] text-accent tracking-widest">AEROSPACE GRADE</span>
                </div>
              </div>

              {/* Additional images row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="relative rounded-2xl overflow-hidden border border-accent/20 group">
                  <img
                    src="/assets/images/helicopter-h130-1783346981245.jpg"
                    alt="Aerospace helicopter component"
                    className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020c14]/60 via-transparent to-transparent" />
                </div>
                <div className="relative rounded-2xl overflow-hidden border border-accent/20 group">
                  <img
                    src="/assets/images/a3f01e3de9da9a0b8d8b51b90d50d93b-1783324608995.jpg"
                    alt="Aerospace structural component"
                    className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020c14]/60 via-transparent to-transparent" />
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 bg-gradient-to-r from-accent/10 to-mission-orange/10 border border-accent/30 rounded-2xl p-6 flex items-center space-x-6">
                  <div className="w-16 h-16 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="RocketLaunchIcon" size={32} className="text-accent" />
                  </div>
                  <div>
                    <p className="font-headline text-4xl font-bold text-accent">200+</p>
                    <p className="font-body text-sm text-technical-gray">Commercial Aviation Projects Delivered</p>
                  </div>
                </div>
                <div className="bg-card/60 border border-border rounded-2xl p-6 hover:border-mission-orange/50 transition-all duration-300">
                  <p className="font-headline text-3xl font-bold text-mission-orange mb-1">30+</p>
                  <p className="font-body text-xs text-technical-gray">Defence Platform Programs</p>
                </div>
                <div className="bg-card/60 border border-border rounded-2xl p-6 hover:border-aerospace-green/50 transition-all duration-300">
                  <p className="font-headline text-3xl font-bold text-aerospace-green mb-1">10+</p>
                  <p className="font-body text-xs text-technical-gray">Space Programs Supported</p>
                </div>
                <div className="bg-card/60 border border-border rounded-2xl p-6 hover:border-accent/50 transition-all duration-300">
                  <p className="font-headline text-3xl font-bold text-accent mb-1">20+</p>
                  <p className="font-body text-xs text-technical-gray">Material Grades Processed</p>
                </div>
                <div className="bg-card/60 border border-border rounded-2xl p-6 hover:border-accent/50 transition-all duration-300">
                  <p className="font-headline text-3xl font-bold text-accent mb-1">99.97%</p>
                  <p className="font-body text-xs text-technical-gray">Quality Success Rate</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 3: SECTORS ── */}
      <Slide slideId="sectors" className="bg-[#030f1a] py-24">
        <GlowOrb className="bottom-0 left-1/4 w-[500px] h-[300px] bg-mission-orange/6" />
        <GlowOrb className="top-0 right-1/3 w-[400px] h-[400px] bg-accent/5" />
        <GridBg />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-px bg-mission-orange" />
            <span className="font-cta text-xs text-mission-orange tracking-widest">SLIDE 03 · SECTORS WE SERVE</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Four Verticals.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-mission-orange to-accent">
              One Standard.
            </span>
          </h2>
          <p className="font-body text-lg text-technical-gray mb-12 max-w-2xl">
            Precision manufacturing across every critical aerospace domain — from commercial airliners to deep-space launchers.
          </p>

          {/* Asymmetric bento grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Commercial — large */}
            <div className="lg:col-span-5 bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/30 rounded-2xl overflow-hidden hover:border-accent/60 transition-all duration-300 hover:shadow-glow group">
              <div className="relative h-36 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1543903905-cee4ab46985c"
                  alt="Commercial Airbus aircraft in flight over clouds"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-70" />
                
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#030f1a]/90" />
                <div className="absolute bottom-3 left-4 flex items-center space-x-3">
                  <div className="w-10 h-10 bg-accent/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-accent/30">
                    <Icon name="PaperAirplaneIcon" size={20} className="text-accent" />
                  </div>
                  <Badge text="AIRBUS APPROVED" color="green" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-headline text-2xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors">
                  Commercial Aircraft | Aerospace
                </h3>
                <p className="font-body text-sm text-technical-gray mb-5 leading-relaxed">
                  Approved supplier for Airbus A320 cabin structural components, sheet metal fabrications, and structural brackets. Rafale Class 2 & 3 precision machined parts.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Airbus A320', 'Rafale Program', 'Sheet Metal', 'Structural Brackets'].map((t) =>
                  <span key={t} className="px-2 py-1 bg-accent/10 border border-accent/20 rounded-md font-cta text-[10px] text-accent">
                      {t}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Defence */}
            <div className="lg:col-span-7 bg-gradient-to-br from-mission-orange/10 to-mission-orange/5 border border-mission-orange/30 rounded-2xl overflow-hidden hover:border-mission-orange/60 transition-all duration-300 group">
              <div className="relative h-36 overflow-hidden">
                <img
                  src="/assets/images/defence-systems-manufacturing.png"
                  alt="Precision machined defence systems components in manufacturing facility"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-65" />
                
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#030f1a]/90" />
                <div className="absolute bottom-3 left-4 flex items-center space-x-3">
                  <div className="w-10 h-10 bg-mission-orange/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-mission-orange/30">
                    <Icon name="ShieldCheckIcon" size={20} className="text-mission-orange" />
                  </div>
                  <Badge text="DEFENCE CERTIFIED" color="orange" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-headline text-2xl font-bold text-foreground mb-3 group-hover:text-mission-orange transition-colors">
                  Defence Systems
                </h3>
                <p className="font-body text-sm text-technical-gray mb-5 leading-relaxed">
                  Mission-critical structural, fuselage, and system components for India's frontline defence platforms — LCA Tejas, Sukhoi Su-30 MKI, UAV programs, and reverse engineering of legacy components.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {['LCA Tejas', 'Sukhoi Su-30 MKI', 'UAV Programs', 'Reverse Engineering'].map((t) =>
                  <div key={t} className="flex items-center space-x-2">
                      <span className="w-1.5 h-1.5 bg-mission-orange rounded-full flex-shrink-0" />
                      <span className="font-body text-xs text-technical-gray">{t}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Space */}
            <div className="lg:col-span-7 bg-gradient-to-br from-aerospace-green/10 to-aerospace-green/5 border border-aerospace-green/30 rounded-2xl overflow-hidden hover:border-aerospace-green/60 transition-all duration-300 group">
              <div className="relative h-36 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1457364983758-510f8afa9f5f"
                  alt="Rocket launch vehicle lifting off with fire and smoke for space mission"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-65" />
                
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#030f1a]/90" />
                <div className="absolute bottom-3 left-4 flex items-center space-x-3">
                  <div className="w-10 h-10 bg-aerospace-green/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-aerospace-green/30">
                    <Icon name="RocketLaunchIcon" size={20} className="text-aerospace-green" />
                  </div>
                  <Badge text="SPACE GRADE" color="green" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-headline text-2xl font-bold text-foreground mb-3 group-hover:text-aerospace-green transition-colors">
                  Space Systems
                </h3>
                <p className="font-body text-sm text-technical-gray mb-5 leading-relaxed">
                  Precision-engineered structural and support systems for satellite platforms and launch vehicles. Satellite structural frames, payload support structures, launcher components, and stage separation hardware.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {['Satellite Frames', 'Launch Vehicles', 'Payload Structures', 'Stage Separation'].map((t) =>
                  <div key={t} className="flex items-center space-x-2">
                      <span className="w-1.5 h-1.5 bg-aerospace-green rounded-full flex-shrink-0" />
                      <span className="font-body text-xs text-technical-gray">{t}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Robotics */}
            <div className="lg:col-span-5 bg-gradient-to-br from-accent/8 to-[#030f1a] border border-accent/20 rounded-2xl overflow-hidden hover:border-accent/50 transition-all duration-300 group">
              <div className="relative h-36 overflow-hidden">
                <img
                  src="/assets/images/robotics-automation-hitech.png"
                  alt="Hi-tech industrial robotic arms in futuristic automation manufacturing facility"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-60" />
                
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#030f1a]/90" />
                <div className="absolute bottom-3 left-4">
                  <div className="w-10 h-10 bg-accent/15 rounded-xl flex items-center justify-center backdrop-blur-sm border border-accent/25">
                    <Icon name="CpuChipIcon" size={20} className="text-accent" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-headline text-2xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors">
                  Robotics & Automation
                </h3>
                <p className="font-body text-sm text-technical-gray mb-5 leading-relaxed">
                  High-precision structural parts for robotic arm systems and industrial automation platforms.
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Arm Link Parts', 'Joint Housing', 'End-Effector Mounts', 'Frame Chassis'].map((t) =>
                  <span key={t} className="px-2 py-1 bg-accent/10 border border-accent/20 rounded-md font-cta text-[10px] text-accent">
                      {t}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 4: KEY PROGRAMS ── */}
      <Slide slideId="programs" className="bg-gradient-to-b from-[#030f1a] to-[#020c14] py-24">
        <GridBg />
        <GlowOrb className="top-1/3 right-0 w-[400px] h-[400px] bg-accent/5" />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-px bg-aerospace-green" />
            <span className="font-cta text-xs text-aerospace-green tracking-widest">SLIDE 04 · KEY PROGRAMS</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Programs We{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-aerospace-green to-accent">
              Power
            </span>
          </h2>
          <p className="font-body text-lg text-technical-gray mb-12 max-w-2xl">
            Approved and active supplier across India's most critical aerospace and defence programs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[
            {
              program: 'Airbus A320',
              type: 'Commercial Aviation',
              parts: ['Cabin Crew Structural Components', 'Sheet Metal Fabrications', 'Structural Brackets & Panels'],
              approval: 'Airbus Approved Supplier',
              color: 'accent',
              icon: 'PaperAirplaneIcon'
            },
            {
              program: 'Rafale Fighter',
              type: 'Commercial / Defence',
              parts: ['Class 2 Structural Parts', 'Class 3 Structural Parts', 'Precision Machined Components'],
              approval: 'Rafale Approved – Class 2 & 3',
              color: 'orange',
              icon: 'BoltIcon'
            },
            {
              program: 'LCA Tejas',
              type: 'Defence — Fighter Jet',
              parts: ['Structural Components', 'Window Frames', 'Wing Components', 'Fuselage Assemblies'],
              approval: 'Active Supplier',
              color: 'orange',
              icon: 'ShieldCheckIcon'
            },
            {
              program: 'Sukhoi Su-30 MKI',
              type: 'Defence — Multi-Role Fighter',
              parts: ['Structural Components', 'Fuselage Parts', 'Precision Machined Assemblies'],
              approval: 'Active Supplier',
              color: 'orange',
              icon: 'ShieldCheckIcon'
            },
            {
              program: 'UAV Programs',
              type: 'Defence — Unmanned Systems',
              parts: ['Engine Complete Assembly', 'Structural Components', 'Remote Weapon System Parts'],
              approval: 'Active Supplier',
              color: 'green',
              icon: 'CpuChipIcon'
            },
            {
              program: 'Satellite & Launch Vehicles',
              type: 'Space Systems',
              parts: ['Satellite Structural Frames', 'Launcher Components', 'Stage Separation Hardware'],
              approval: 'Space Grade Certified',
              color: 'green',
              icon: 'RocketLaunchIcon'
            }].
            map((item) =>
            <div
              key={item.program}
              className={`relative bg-card/50 backdrop-blur-sm border rounded-2xl p-6 hover:shadow-glow transition-all duration-300 group overflow-hidden ${
              item.color === 'orange' ? 'border-mission-orange/20 hover:border-mission-orange/50' :
              item.color === 'green' ? 'border-aerospace-green/20 hover:border-aerospace-green/50' : 'border-accent/20 hover:border-accent/50'}`
              }>
              
                {/* Corner accent */}
                <div
                className={`absolute top-0 right-0 w-16 h-16 rounded-bl-3xl opacity-20 ${
                item.color === 'orange' ? 'bg-mission-orange' : item.color === 'green' ? 'bg-aerospace-green' : 'bg-accent'}`
                } />
              

                <div className="flex items-start justify-between mb-4">
                  <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  item.color === 'orange' ? 'bg-mission-orange/20' :
                  item.color === 'green' ? 'bg-aerospace-green/20' : 'bg-accent/20'}`
                  }>
                  
                    <Icon
                    name={item.icon as any}
                    size={20}
                    className={
                    item.color === 'orange' ? 'text-mission-orange' :
                    item.color === 'green' ? 'text-aerospace-green' : 'text-accent'
                    } />
                  
                  </div>
                  <span
                  className={`font-cta text-[10px] px-2 py-1 rounded-full border ${
                  item.color === 'orange' ? 'bg-mission-orange/10 border-mission-orange/30 text-mission-orange' :
                  item.color === 'green' ? 'bg-aerospace-green/10 border-aerospace-green/30 text-aerospace-green' : 'bg-accent/10 border-accent/30 text-accent'}`
                  }>
                  
                    {item.approval}
                  </span>
                </div>

                <h3 className="font-headline text-xl font-bold text-foreground mb-1 group-hover:text-accent transition-colors">
                  {item.program}
                </h3>
                <p className="font-cta text-xs text-technical-gray mb-4 tracking-wider">{item.type}</p>

                <ul className="space-y-1.5">
                  {item.parts.map((p) =>
                <li key={p} className="flex items-center space-x-2">
                      <span
                    className={`w-1 h-1 rounded-full flex-shrink-0 ${
                    item.color === 'orange' ? 'bg-mission-orange' : item.color === 'green' ? 'bg-aerospace-green' : 'bg-accent'}`
                    } />
                  
                      <span className="font-body text-xs text-technical-gray">{p}</span>
                    </li>
                )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 5: HALAMANEE AEROSPACE PARTNERSHIP ── */}
      <Slide slideId="partnership" className="bg-gradient-to-b from-[#020c14] via-[#030f1a] to-[#020c14] py-24">
        <GridBg />
        <GlowOrb className="top-0 left-1/3 w-[600px] h-[400px] bg-accent/7" />
        <GlowOrb className="bottom-0 right-1/4 w-[500px] h-[400px] bg-mission-orange/6" />
        <ScanLine />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20">
          {/* Slide label */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-px bg-accent" />
            <span className="font-cta text-xs text-accent tracking-widest">SLIDE 05 · STRATEGIC PARTNERSHIP</span>
          </div>

          {/* Heading */}
          <div className="mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-mission-orange/10 rounded-full border border-mission-orange/30 mb-5">
              <span className="w-2 h-2 bg-mission-orange rounded-full animate-pulse" />
              <span className="font-cta text-xs text-mission-orange tracking-widest">JOINT VENTURE · HALAMANEE AEROSPACE GROUPS</span>
            </div>
            <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
              Strategic Project Management &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-mission-orange">
                Manufacturing Partner
              </span>
            </h2>
            <p className="font-body text-lg text-technical-gray max-w-3xl leading-relaxed">
              Wings Bharath is the exclusive strategic project management and manufacturing partner for Halamanee Aerospace Groups — developed as a joint venture with a vision to serve clients at global aerospace and defence international standards.
            </p>
          </div>

          {/* Main content grid */}
          <div className="grid lg:grid-cols-2 gap-10 mb-12">
            {/* Left: Vision & Value Proposition */}
            <div className="flex flex-col gap-6">
              {/* Vision card */}
              <div className="bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/30 rounded-2xl p-7 hover:border-accent/60 transition-all duration-300">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-accent/30">
                    <Icon name="EyeIcon" size={24} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="font-headline text-xl font-bold text-foreground mb-1">Our Vision</h3>
                    <p className="font-body text-sm leading-relaxed font-medium text-[rgba(180,245,248,1)]">
                      To provide seamless, stress-free project management and complete visibility for every project entrusted to Wings Bharath — catering to clients at the highest aerospace and defence global standards.
                    </p>
                  </div>
                </div>
              </div>

              {/* Complete Ownership card */}
              <div className="bg-gradient-to-br from-mission-orange/10 to-mission-orange/5 border border-mission-orange/30 rounded-2xl p-7 hover:border-mission-orange/60 transition-all duration-300">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-mission-orange/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-mission-orange/30">
                    <Icon name="ShieldCheckIcon" size={24} className="text-mission-orange" />
                  </div>
                  <div>
                    <h3 className="font-headline text-xl font-bold text-foreground mb-1">Complete Ownership</h3>
                    <p className="font-body text-sm leading-relaxed font-medium text-[rgba(173,247,251,1)]">
                      Wings Bharath takes complete ownership of every project from receipt to delivery — making the client's job more efficient and dramatically reducing the risk of missed deliveries that can cause severe financial and reputational damage.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Digital Portal & QA */}
            <div className="flex flex-col gap-6">
              {/* Digital Portal card */}
              <div className="bg-gradient-to-br from-aerospace-green/10 to-aerospace-green/5 border border-aerospace-green/30 rounded-2xl p-7 hover:border-aerospace-green/60 transition-all duration-300">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-12 h-12 bg-aerospace-green/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-aerospace-green/30">
                    <Icon name="ComputerDesktopIcon" size={24} className="text-aerospace-green" />
                  </div>
                  <div>
                    <h3 className="font-headline text-xl font-bold text-foreground mb-1">Fully Digitalized Portal</h3>
                    <p className="font-body text-sm leading-relaxed font-medium text-[rgba(145,241,243,1)]">
                      Our portal is completely digitalized — clients can track their components in real time at required aerospace standards, ensuring full traceability and the highest QA compliance throughout the manufacturing lifecycle.
                    </p>
                  </div>
                </div>
              </div>

              {/* Real-time tracking card */}
              <div className="bg-card/60 border border-border rounded-2xl p-7 hover:border-accent/40 transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-accent/15 rounded-xl flex items-center justify-center flex-shrink-0 border border-accent/25">
                    <Icon name="ChartBarIcon" size={24} className="text-accent" />
                  </div>
                  <div>
                    <h3 className="font-headline text-xl font-bold text-foreground mb-1">Real-Time Visibility</h3>
                    <p className="font-body text-sm leading-relaxed font-medium text-[rgba(166,245,252,1)]">
                      Live component tracking, milestone updates, and QA status — all accessible in one place, ensuring zero surprises and maximum confidence in every delivery to your customers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: Key pillars strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
            { icon: 'HandshakeIcon', label: 'Joint Venture', sub: 'Halamanee Aerospace Groups', color: 'accent' },
            { icon: 'CogIcon', label: 'Seamless PM', sub: 'Stress-free project management', color: 'orange' },
            { icon: 'MagnifyingGlassIcon', label: 'Full Traceability', sub: 'Real-time component tracking', color: 'green' },
            { icon: 'StarIcon', label: 'Highest QA', sub: 'Aerospace & defence standards', color: 'accent' }].
            map((item) =>
            <div
              key={item.label}
              className={`flex flex-col items-center text-center p-5 rounded-2xl border transition-all duration-300 ${
              item.color === 'orange' ? 'bg-mission-orange/8 border-mission-orange/25 hover:border-mission-orange/50' :
              item.color === 'green' ? 'bg-aerospace-green/8 border-aerospace-green/25 hover:border-aerospace-green/50' : 'bg-accent/8 border-accent/25 hover:border-accent/50'}`
              }>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
              item.color === 'orange' ? 'bg-mission-orange/20' : item.color === 'green' ? 'bg-aerospace-green/20' : 'bg-accent/20'}`
              }>
                  <Icon
                  name={item.icon as any}
                  size={20}
                  className={item.color === 'orange' ? 'text-mission-orange' : item.color === 'green' ? 'text-aerospace-green' : 'text-accent'} />

                </div>
                <p className={`font-headline text-sm font-bold mb-1 ${
              item.color === 'orange' ? 'text-mission-orange' : item.color === 'green' ? 'text-aerospace-green' : 'text-accent'}`
              }>{item.label}</p>
                <p className="font-body text-[11px] text-technical-gray leading-snug">{item.sub}</p>
              </div>
            )}
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 6: PROJECT FLOW ── */}
      <Slide slideId="flowchart" className="bg-[#020c14] py-24">
        <GridBg />
        <GlowOrb className="top-0 left-1/4 w-[600px] h-[400px] bg-accent/6" />
        <GlowOrb className="bottom-0 right-1/4 w-[500px] h-[400px] bg-mission-orange/5" />
        <GlowOrb className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[300px] bg-accent/3" />
        <ScanLine />

        {/* Decorative corner brackets */}
        <div className="absolute top-24 left-8 w-8 h-8 border-t-2 border-l-2 border-accent/30 pointer-events-none" />
        <div className="absolute top-24 right-8 w-8 h-8 border-t-2 border-r-2 border-accent/30 pointer-events-none" />
        <div className="absolute bottom-8 left-8 w-8 h-8 border-b-2 border-l-2 border-accent/30 pointer-events-none" />
        <div className="absolute bottom-8 right-8 w-8 h-8 border-b-2 border-r-2 border-accent/30 pointer-events-none" />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20 pb-10">
          {/* Slide label */}
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-px bg-accent" />
            <span className="font-cta text-xs text-accent tracking-widest">SLIDE 06 · PROJECT EXECUTION FLOW</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-3">
            Strategic{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-mission-orange">
              Project Flow
            </span>
          </h2>
          <p className="font-body text-base text-technical-gray mb-12 max-w-2xl">
            A joint-venture execution model — seamless, traceable, and transparent from client order to final delivery.
          </p>

          {/* ── PROJECT PROCESS FLOW ── */}
          <div className="mb-14">
            {/* Scope legend */}
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-accent shadow-[0_0_8px_rgba(0,212,255,0.8)]" />
                <span className="font-cta text-[10px] tracking-widest text-accent uppercase">Wings Bharath Scope</span>
              </div>
              <div className="w-px h-4 bg-white/10" />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-mission-orange shadow-[0_0_8px_rgba(238,149,114,0.8)]" />
                <span className="font-cta text-[10px] tracking-widest text-mission-orange uppercase">In-House Manufacturing</span>
              </div>
              <div className="w-px h-4 bg-white/10" />
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-aerospace-green shadow-[0_0_8px_rgba(0,255,136,0.8)]" />
                <span className="font-cta text-[10px] tracking-widest text-aerospace-green uppercase">Sourcing Partners</span>
              </div>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-0 items-start">
              {[
              {
                step: '01',
                label: 'RFQ Submission',
                desc: 'Client submits requirements',
                icon: 'DocumentTextIcon',
                scope: 'wings',
                scopeLabel: 'Wings Bharath'
              },
              {
                step: '02',
                label: 'Engineering Review',
                desc: 'Technical feasibility & DFM analysis',
                icon: 'CpuChipIcon',
                scope: 'wings',
                scopeLabel: 'Wings Bharath'
              },
              {
                step: '03',
                label: 'Quote & Approval',
                desc: 'Costing, timeline & client sign-off',
                icon: 'CheckBadgeIcon',
                scope: 'wings',
                scopeLabel: 'Wings Bharath'
              },
              {
                step: '04',
                label: 'Manufacturing',
                desc: 'Precision production by group entities & sourcing partners',
                icon: 'CogIcon',
                scope: 'inhouse',
                scopeLabel: 'In-House + Partners'
              },
              {
                step: '05',
                label: 'Quality Assurance',
                desc: 'AS9100 / NADCAP compliant checks — Wings Bharath owned',
                icon: 'ShieldCheckIcon',
                scope: 'wings',
                scopeLabel: 'Wings Bharath'
              },
              {
                step: '06',
                label: 'On-Time Delivery',
                desc: 'Traceable delivery with portal update — Wings Bharath owned',
                icon: 'TruckIcon',
                scope: 'wings',
                scopeLabel: 'Wings Bharath'
              }].
              map((s, i, arr) => {
                const isWings = s.scope === 'wings';
                const isInhouse = s.scope === 'inhouse';
                const color = isWings ? 'accent' : isInhouse ? 'mission-orange' : 'aerospace-green';
                const borderClass = isWings ?
                'border-accent/50 hover:border-accent hover:shadow-[0_0_25px_rgba(0,212,255,0.3)]' :
                isInhouse ?
                'border-mission-orange/50 hover:border-mission-orange hover:shadow-[0_0_25px_rgba(238,149,114,0.3)]' :
                'border-aerospace-green/50 hover:border-aerospace-green hover:shadow-[0_0_25px_rgba(0,255,136,0.3)]';
                const glowClass = isWings ? 'bg-accent/10' : isInhouse ? 'bg-mission-orange/10' : 'bg-aerospace-green/10';
                const iconBg = isWings ? 'bg-accent/15 border-accent/40' : isInhouse ? 'bg-mission-orange/15 border-mission-orange/40' : 'bg-aerospace-green/15 border-aerospace-green/40';
                const iconColor = isWings ? 'text-accent' : isInhouse ? 'text-mission-orange' : 'text-aerospace-green';
                const stepColor = isWings ? 'text-accent' : isInhouse ? 'text-mission-orange' : 'text-aerospace-green';
                const badgeBg = isWings ? 'bg-accent/10 border-accent/30 text-accent' : isInhouse ? 'bg-mission-orange/10 border-mission-orange/30 text-mission-orange' : 'bg-aerospace-green/10 border-aerospace-green/30 text-aerospace-green';
                const topBar = isWings ? 'via-accent/70' : isInhouse ? 'via-mission-orange/70' : 'via-aerospace-green/70';
                return (
                  <div key={s.step} className="flex lg:flex-row items-stretch">
                    {/* Card */}
                    <div className="relative group flex-1">
                      <div className={`absolute inset-0 rounded-xl ${glowClass} blur-lg group-hover:opacity-150 transition-all duration-500`} />
                      <div className={`relative bg-card/60 backdrop-blur-md border-2 ${borderClass} rounded-xl p-4 flex flex-col items-center text-center transition-all duration-300 h-full`}>
                        <div className={`absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent ${topBar} to-transparent`} />
                        <span className={`font-cta text-[9px] tracking-[0.2em] ${stepColor} mb-2`}>{s.step}</span>
                        <div className={`w-10 h-10 rounded-lg ${iconBg} border flex items-center justify-center mb-3`}>
                          <Icon name={s.icon as any} size={18} className={iconColor} />
                        </div>
                        <p className="font-headline text-sm font-bold text-foreground mb-1 leading-tight">{s.label}</p>
                        <p className="font-body text-[10px] text-technical-gray leading-relaxed mb-3">{s.desc}</p>
                        <span className={`font-cta text-[8px] tracking-widest border rounded-full px-2 py-0.5 ${badgeBg}`}>{s.scopeLabel}</span>
                      </div>
                    </div>
                    {/* Connector arrow (between cards, not after last) */}
                    {i < arr.length - 1 &&
                    <div className="hidden lg:flex items-center justify-center w-5 flex-shrink-0 relative z-10">
                        <svg width="20" height="12" viewBox="0 0 20 12" fill="none" className={isWings ? 'text-accent/60' : isInhouse ? 'text-mission-orange/60' : 'text-aerospace-green/60'}>
                          <path d="M0 6H16M16 6L11 1M16 6L11 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    }
                  </div>);

              })}
            </div>

            {/* Scope highlight bar */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-accent/5 border border-accent/25 rounded-xl px-5 py-3 flex items-center gap-3">
                <div className="w-2 h-10 rounded-full bg-gradient-to-b from-accent to-accent/30 flex-shrink-0" />
                <div>
                  <p className="font-cta text-[9px] tracking-widest text-accent uppercase mb-0.5">Wings Bharath Scope</p>
                  <p className="font-body text-xs text-foreground/80 font-medium">Steps 01 – 03 · 05 – 06</p>
                  <p className="font-body text-[10px] text-technical-gray">RFQ · Engineering · Quote · Quality Assurance · Delivery</p>
                </div>
              </div>
              <div className="bg-mission-orange/5 border border-mission-orange/25 rounded-xl px-5 py-3 flex items-center gap-3">
                <div className="w-2 h-10 rounded-full bg-gradient-to-b from-mission-orange to-mission-orange/30 flex-shrink-0" />
                <div>
                  <p className="font-cta text-[9px] tracking-widest text-mission-orange uppercase mb-0.5">In-House Manufacturing</p>
                  <p className="font-body text-xs text-foreground/80 font-medium">Step 04</p>
                  <p className="font-body text-[10px] text-technical-gray">Halamanee Group of Companies</p>
                </div>
              </div>
              <div className="bg-aerospace-green/5 border border-aerospace-green/25 rounded-xl px-5 py-3 flex items-center gap-3">
                <div className="w-2 h-10 rounded-full bg-gradient-to-b from-aerospace-green to-aerospace-green/30 flex-shrink-0" />
                <div>
                  <p className="font-cta text-[9px] tracking-widest text-aerospace-green uppercase mb-0.5">Sourcing Partners</p>
                  <p className="font-body text-xs text-foreground/80 font-medium">Step 04 (Co-Manufacturing)</p>
                  <p className="font-body text-[10px] text-technical-gray">Manufacturing · Sub-Assemblies · Components</p>
                </div>
              </div>
            </div>

            {/* ── WINGS BHARATH OWNERSHIP PROMISE ── */}
            <div className="mt-8 relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-[#020c14]/95 via-[#0a1a2e]/90 to-[#020c14]/95 backdrop-blur-md p-6 lg:p-8">
              {/* Top glow bar */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />
              {/* Bottom glow bar */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-mission-orange/50 to-transparent" />
              {/* Background glow */}
              <div className="absolute inset-0 bg-accent/3 blur-3xl pointer-events-none" />

              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-accent/15 border border-accent/40 flex items-center justify-center flex-shrink-0">
                    <Icon name="ShieldCheckIcon" size={16} className="text-accent" />
                  </div>
                  <div>
                    <span className="font-cta text-[9px] tracking-[0.2em] text-accent uppercase">Our Unique Strength</span>
                    <h3 className="font-headline text-lg lg:text-xl font-bold text-foreground leading-tight">
                      Complete Ownership. Zero Hassle. Total Transparency.
                    </h3>
                  </div>
                </div>

                <p className="font-body text-sm text-foreground/85 leading-relaxed mb-6 max-w-3xl">
                  Once you hand over your project to Wings Bharath, <span className="text-accent font-semibold">we take complete ownership</span> — from engineering to quality assurance to on-time delivery. No stressful follow-ups. No ambiguity. Just seamless, accountable execution with every milestone tracked and documented.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                  {
                    icon: 'ShieldCheckIcon',
                    color: 'accent',
                    title: 'Quality Assurance Owned',
                    desc: 'Wings Bharath directly owns and governs every quality checkpoint — AS9100 & NADCAP compliant, no exceptions.'
                  },
                  {
                    icon: 'TruckIcon',
                    color: 'orange',
                    title: 'On-Time Delivery Guaranteed',
                    desc: 'We own the delivery commitment end-to-end. Your timelines are our responsibility — tracked, managed, and met.'
                  },
                  {
                    icon: 'MagnifyingGlassIcon',
                    color: 'green',
                    title: 'High Traceability',
                    desc: 'Every component, every process stage — fully traceable with digital documentation. Know exactly where your project stands.'
                  },
                  {
                    icon: 'EyeIcon',
                    color: 'accent',
                    title: '100% Client Transparency',
                    desc: 'Your project, your visibility. Real-time access to current stage, process status, and progress across all components via our client portal.'
                  }].
                  map((item) =>
                  <div
                    key={item.title}
                    className={`flex flex-col gap-2 rounded-xl p-4 border transition-all duration-300 ${
                    item.color === 'green' ? 'bg-aerospace-green/5 border-aerospace-green/20 hover:border-aerospace-green/40' :
                    item.color === 'orange' ? 'bg-mission-orange/5 border-mission-orange/20 hover:border-mission-orange/40' : 'bg-accent/5 border-accent/20 hover:border-accent/40'}`
                    }>
                    
                      <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.color === 'green' ? 'bg-aerospace-green/15' :
                      item.color === 'orange' ? 'bg-mission-orange/15' : 'bg-accent/15'}`
                      }>
                      
                        <Icon
                        name={item.icon as any}
                        size={15}
                        className={
                        item.color === 'green' ? 'text-aerospace-green' :
                        item.color === 'orange' ? 'text-mission-orange' : 'text-accent'
                        } />
                      
                      </div>
                      <p
                      className={`font-cta text-[9px] tracking-widest uppercase ${
                      item.color === 'green' ? 'text-aerospace-green' :
                      item.color === 'orange' ? 'text-mission-orange' : 'text-accent'}`
                      }>
                      
                        {item.title}
                      </p>
                      <p className="font-body text-[10px] text-technical-gray leading-relaxed">{item.desc}</p>
                    </div>
                  )}
                </div>

                <div className="mt-5 pt-4 border-t border-accent/15 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                    <span className="font-body text-xs text-foreground/70 italic">
                      &ldquo;We believe every client has the right to know exactly where their project or component is at every stage and process — and we make that view a reality.&rdquo;
                    </span>
                  </div>
                  <span className="font-cta text-[8px] tracking-widest text-accent border border-accent/30 rounded-full px-3 py-1 bg-accent/5 whitespace-nowrap flex-shrink-0">WINGS BHARATH PROMISE</span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="mt-12 mb-10 flex items-center gap-4">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />
              <span className="font-cta text-[9px] tracking-[0.25em] text-accent/50 uppercase">Organisational Structure</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-accent/20 to-transparent" />
            </div>
          </div>

          {/* ── FLOWCHART ── */}
          <div className="flex flex-col items-center gap-0">

            {/* ── LEVEL 1: CLIENT ── */}
            <div className="flex flex-col items-center">
              <div className="relative group">
                {/* Outer glow ring */}
                <div className="absolute inset-0 rounded-2xl bg-accent/10 blur-xl group-hover:bg-accent/20 transition-all duration-500" />
                <div className="relative bg-card/70 backdrop-blur-md border-2 border-accent/50 rounded-2xl px-10 py-5 flex flex-col items-center gap-2 hover:border-accent transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,212,255,0.25)] min-w-[220px]">
                  <span className="font-cta text-[9px] tracking-[0.2em] text-accent/70 uppercase">Order Initiator</span>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent/15 border border-accent/40 flex items-center justify-center">
                      <Icon name="UserGroupIcon" size={16} className="text-accent" />
                    </div>
                    <span className="font-headline text-xl font-bold text-foreground tracking-wide">CLIENT</span>
                  </div>
                  <span className="font-body text-[10px] text-technical-gray text-center">Places Order / RFQ</span>
                  {/* Pulse dot */}
                  <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-accent animate-ping opacity-60" />
                  <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-accent" />
                </div>
              </div>

              {/* Arrow down */}
              <div className="flex flex-col items-center my-1">
                <div className="w-px h-8 bg-gradient-to-b from-accent/80 to-accent/30" />
                <svg width="14" height="8" viewBox="0 0 14 8" fill="none" className="text-accent">
                  <path d="M7 8L0.937822 0.5H13.0622L7 8Z" fill="currentColor" fillOpacity="0.8" />
                </svg>
              </div>
            </div>

            {/* ── LEVEL 2: WINGS BHARATH ── */}
            <div className="flex flex-col items-center w-full max-w-2xl">
              <div className="relative group w-full">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-accent/10 via-mission-orange/8 to-accent/10 blur-xl group-hover:opacity-150 transition-all duration-500" />
                <div className="relative bg-gradient-to-br from-[#0a1a2e]/90 to-[#020c14]/90 backdrop-blur-md border-2 border-accent/60 rounded-2xl px-8 py-6 hover:border-accent hover:shadow-[0_0_40px_rgba(0,212,255,0.3)] transition-all duration-300">
                  {/* Top accent bar */}
                  <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-accent to-transparent" />

                  <div className="flex flex-col lg:flex-row items-center gap-5">
                    {/* Logo */}
                    <div className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-accent/30 bg-white/5 flex items-center justify-center p-1">
                      <img
                        src="/assets/images/Project__1_-1783399871045.png"
                        alt="Wings Bharath & Halamanee Aerospace Group logos"
                        className="w-full h-full object-contain"
                        style={{ filter: 'brightness(1.1) contrast(1.05)' }} />
                      
                    </div>

                    {/* Info */}
                    <div className="flex-1 text-center lg:text-left">
                      <div className="flex items-center justify-center lg:justify-start gap-2 mb-1">
                        <span className="font-cta text-[9px] tracking-[0.2em] text-accent uppercase bg-accent/10 border border-accent/30 rounded-full px-3 py-0.5">Strategic Partner · Joint Venture</span>
                      </div>
                      <h3 className="font-headline text-2xl lg:text-3xl font-bold text-foreground mb-1">Wings Bharath</h3>
                      <p className="font-body text-xs text-accent/80 font-medium mb-2">Strategic Project Management &amp; Manufacturing Partner</p>
                      <p className="font-body text-[11px] text-technical-gray leading-relaxed max-w-lg">
                        A joint venture with Halamanee Aerospace Group of Companies — delivering hi-tech seamless project management with precise, traceable &amp; transparent execution.
                      </p>
                    </div>

                    {/* Right badges */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {['PROJECT MGMT', 'TRACEABILITY', 'TRANSPARENCY'].map((b) =>
                      <span key={b} className="font-cta text-[8px] tracking-widest text-accent/70 border border-accent/20 rounded px-2 py-1 bg-accent/5 text-center">{b}</span>
                      )}
                    </div>
                  </div>

                  {/* Bottom accent bar */}
                  <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-mission-orange/50 to-transparent" />
                </div>
              </div>

              {/* Arrow + label */}
              <div className="flex flex-col items-center my-1">
                <div className="w-px h-8 bg-gradient-to-b from-mission-orange/80 to-mission-orange/30" />
                <svg width="14" height="8" viewBox="0 0 14 8" fill="none" className="text-mission-orange">
                  <path d="M7 8L0.937822 0.5H13.0622L7 8Z" fill="currentColor" fillOpacity="0.8" />
                </svg>
              </div>
            </div>

            {/* ── LEVEL 3: TWO BRANCHES ── */}
            <div className="w-full max-w-5xl">
              {/* Horizontal connector */}
              <div className="hidden lg:flex items-center justify-center mb-0 relative h-6">
                <div className="absolute left-[25%] right-[25%] top-1/2 h-px bg-gradient-to-r from-mission-orange/60 via-accent/40 to-aerospace-green/60" />
                <div className="absolute left-[25%] top-0 w-px h-full bg-mission-orange/40" />
                <div className="absolute right-[25%] top-0 w-px h-full bg-aerospace-green/40" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* ── BRANCH A: IN-HOUSE MANUFACTURING ── */}
                <div className="flex flex-col items-center">
                  {/* Mobile arrow */}
                  <div className="lg:hidden flex flex-col items-center mb-3">
                    <div className="w-px h-6 bg-mission-orange/60" />
                    <svg width="12" height="7" viewBox="0 0 14 8" fill="none" className="text-mission-orange">
                      <path d="M7 8L0.937822 0.5H13.0622L7 8Z" fill="currentColor" fillOpacity="0.8" />
                    </svg>
                  </div>

                  <div className="relative group w-full">
                    <div className="absolute inset-0 rounded-2xl bg-mission-orange/8 blur-lg group-hover:bg-mission-orange/15 transition-all duration-500" />
                    <div className="relative bg-card/60 backdrop-blur-md border-2 border-mission-orange/40 rounded-2xl p-5 hover:border-mission-orange/70 hover:shadow-[0_0_30px_rgba(238,149,114,0.2)] transition-all duration-300 h-full">
                      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-mission-orange/60 to-transparent" />

                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-mission-orange/15 border border-mission-orange/40 flex items-center justify-center flex-shrink-0">
                          <Icon name="CogIcon" size={18} className="text-mission-orange" />
                        </div>
                        <div>
                          <span className="font-cta text-[8px] tracking-widest text-mission-orange/70 uppercase block">Branch A</span>
                          <h4 className="font-headline text-base font-bold text-foreground leading-tight">In-House Manufacturing</h4>
                        </div>
                      </div>

                      {/* Logo strip + companies */}
                      <div className="mb-4 flex items-center gap-3 bg-white/3 border border-mission-orange/15 rounded-xl p-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden border border-mission-orange/25 bg-white/5 flex-shrink-0 flex items-center justify-center p-0.5">
                          <img
                            src="/assets/images/Project__1_-1783399871045.png"
                            alt="Halamanee Aerospace Group logos"
                            className="w-full h-full object-contain"
                            style={{ filter: 'brightness(1.1)' }} />
                          
                        </div>
                        <div>
                          <p className="font-cta text-[8px] tracking-widest text-mission-orange/60 uppercase mb-1">Halamanee Aerospace Group</p>
                          <p className="font-body text-[10px] text-technical-gray/80 leading-relaxed">Group of Companies</p>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {[
                        { name: 'Halamanee Aerospace', tag: 'STRUCTURES' },
                        { name: 'Halamanee Aerostructures', tag: 'ASSEMBLIES' },
                        { name: 'Halamanee Precisions', tag: 'MACHINING' },
                        { name: 'Skybus Aerospace', tag: 'SYSTEMS' }].
                        map((co) =>
                        <div key={co.name} className="flex items-center justify-between bg-mission-orange/5 border border-mission-orange/15 rounded-lg px-3 py-2 hover:border-mission-orange/35 transition-colors duration-200">
                            <div className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-mission-orange/70" />
                              <span className="font-body text-xs text-foreground/90">{co.name}</span>
                            </div>
                            <span className="font-cta text-[8px] tracking-widest text-mission-orange/60 border border-mission-orange/20 rounded px-1.5 py-0.5">{co.tag}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── BRANCH B: SOURCING ── */}
                <div className="flex flex-col items-center">
                  {/* Mobile arrow */}
                  <div className="lg:hidden flex flex-col items-center mb-3">
                    <div className="w-px h-6 bg-aerospace-green/60" />
                    <svg width="12" height="7" viewBox="0 0 14 8" fill="none" className="text-aerospace-green">
                      <path d="M7 8L0.937822 0.5H13.0622L7 8Z" fill="currentColor" fillOpacity="0.8" />
                    </svg>
                  </div>

                  <div className="relative group w-full">
                    <div className="absolute inset-0 rounded-2xl bg-aerospace-green/8 blur-lg group-hover:bg-aerospace-green/15 transition-all duration-500" />
                    <div className="relative bg-card/60 backdrop-blur-md border-2 border-aerospace-green/40 rounded-2xl p-5 hover:border-aerospace-green/70 hover:shadow-[0_0_30px_rgba(0,255,136,0.2)] transition-all duration-300 h-full">
                      <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-aerospace-green/60 to-transparent" />

                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-lg bg-aerospace-green/15 border border-aerospace-green/40 flex items-center justify-center flex-shrink-0">
                          <Icon name="TruckIcon" size={18} className="text-aerospace-green" />
                        </div>
                        <div>
                          <span className="font-cta text-[8px] tracking-widest text-aerospace-green/70 uppercase block">Branch B</span>
                          <h4 className="font-headline text-base font-bold text-foreground leading-tight">Sourcing with Dedicated Partners</h4>
                        </div>
                      </div>

                      {/* Primary partner */}
                      <div className="mb-4 bg-aerospace-green/5 border border-aerospace-green/20 rounded-xl p-3">
                        <p className="font-cta text-[8px] tracking-widest text-aerospace-green/60 uppercase mb-2">Primary Sourcing Partner</p>
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-lg overflow-hidden border border-aerospace-green/25 bg-white/5 flex-shrink-0 flex items-center justify-center p-0.5">
                            <img
                              src="/assets/images/Project__1_-1783399871045.png"
                              alt="Aero Tech sourcing partner logo"
                              className="w-full h-full object-contain"
                              style={{ filter: 'brightness(1.1)' }} />
                            
                          </div>
                          <div>
                            <p className="font-headline text-sm font-bold text-foreground">Aero Tech</p>
                            <p className="font-body text-[10px] text-technical-gray/80">Qualified Aerospace Sourcing</p>
                          </div>
                        </div>
                      </div>

                      {/* Additional partners */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between bg-aerospace-green/5 border border-aerospace-green/15 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-aerospace-green/70" />
                            <span className="font-body text-xs text-foreground/90">Aero Tech</span>
                          </div>
                          <span className="font-cta text-[8px] tracking-widest text-aerospace-green/60 border border-aerospace-green/20 rounded px-1.5 py-0.5">PRIMARY</span>
                        </div>
                        <div className="flex items-center justify-between bg-aerospace-green/5 border border-aerospace-green/15 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-aerospace-green/40" />
                            <span className="font-body text-xs text-foreground/70">Other Qualified Sourcing Partners</span>
                          </div>
                          <span className="font-cta text-[8px] tracking-widest text-aerospace-green/50 border border-aerospace-green/15 rounded px-1.5 py-0.5">NETWORK</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-aerospace-green/15">
                        <p className="font-body text-[10px] text-technical-gray/70 leading-relaxed">
                          Vetted supply chain partners responsible for manufacturing, raw materials, sub-assemblies &amp; components — all under Wings Bharath quality oversight.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* ── BOTTOM STRIP ── */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
            {
              label: 'COMPLETE PROJECT OWNERSHIP',
              desc: 'Hand over your project once — Wings Bharath takes full ownership from engineering review to final delivery. No follow-ups, no stress.',
              icon: 'CpuChipIcon',
              color: 'accent'
            },
            {
              label: 'HIGH TRACEABILITY & DIGITAL DOCS',
              desc: 'Every milestone, component, and process stage is digitally documented and traceable — giving you full confidence and audit-ready records.',
              icon: 'ChartBarIcon',
              color: 'orange'
            },
            {
              label: 'REAL-TIME CLIENT TRANSPARENCY',
              desc: 'You have every right to know where your project stands. Our client portal gives you live visibility into stage, quality, and delivery status.',
              icon: 'ShieldCheckIcon',
              color: 'green'
            }].
            map((item) =>
            <div
              key={item.label}
              className={`flex items-start space-x-4 bg-card/40 border rounded-xl p-5 transition-all duration-300 ${
              item.color === 'green' ? 'border-aerospace-green/20 hover:border-aerospace-green/40' :
              item.color === 'orange' ? 'border-mission-orange/20 hover:border-mission-orange/40' : 'border-accent/20 hover:border-accent/40'}`
              }>
              
                <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                item.color === 'green' ? 'bg-aerospace-green/15' :
                item.color === 'orange' ? 'bg-mission-orange/15' : 'bg-accent/15'}`
                }>
                
                  <Icon
                  name={item.icon as any}
                  size={18}
                  className={
                  item.color === 'green' ? 'text-aerospace-green' :
                  item.color === 'orange' ? 'text-mission-orange' : 'text-accent'
                  } />
                
                </div>
                <div>
                  <p
                  className={`font-cta text-[10px] tracking-widest mb-1 ${
                  item.color === 'green' ? 'text-aerospace-green' :
                  item.color === 'orange' ? 'text-mission-orange' : 'text-accent'}`
                  }>
                  
                    {item.label}
                  </p>
                  <p className="font-body text-xs text-technical-gray leading-relaxed">{item.desc}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 6: CAPABILITIES ── */}
      <Slide slideId="capabilities" className="bg-[#020c14] py-24">
        <GridBg />
        <GlowOrb className="bottom-0 left-0 w-[500px] h-[400px] bg-accent/6" />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-px bg-accent" />
            <span className="font-cta text-xs text-accent tracking-widest">SLIDE 07 · MANUFACTURING CAPABILITIES</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Built to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-mission-orange">
              Print
            </span>
          </h2>
          <p className="font-body text-lg text-technical-gray mb-12 max-w-2xl">
            End-to-end manufacturing capabilities from raw material to flight-ready assembly.
          </p>

          {/* Visual image strip */}
          <div className="grid grid-cols-3 gap-3 mb-10 rounded-2xl overflow-hidden">
            <div className="relative h-40 overflow-hidden rounded-xl group">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_18523cc5b-1772116690886.png"
                alt="CNC precision machining of aerospace metal components with cutting tools"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-80" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#020c14]/80 to-transparent" />
              <div className="absolute bottom-2 left-3">
                <span className="font-cta text-[9px] text-accent tracking-widest">CNC MACHINING</span>
              </div>
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent" />
            </div>
            <div className="relative h-40 overflow-hidden rounded-xl group">
              <img
                src="https://images.unsplash.com/photo-1735494035570-4b48dd6ae3f5"
                alt="Sheet metal fabrication and welding sparks in aerospace manufacturing"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-80" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#020c14]/80 to-transparent" />
              <div className="absolute bottom-2 left-3">
                <span className="font-cta text-[9px] text-mission-orange tracking-widest">SHEET METAL</span>
              </div>
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-mission-orange/50 to-transparent" />
            </div>
            <div className="relative h-40 overflow-hidden rounded-xl group">
              <img
                src="https://img.rocket.new/generatedImages/rocket_gen_img_159d05828-1783401166944.png"
                alt="Quality inspection and measurement of precision aerospace structural assembly"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-80" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#020c14]/80 to-transparent" />
              <div className="absolute bottom-2 left-3">
                <span className="font-cta text-[9px] text-aerospace-green tracking-widest">QUALITY INSPECTION</span>
              </div>
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-aerospace-green/50 to-transparent" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Left: capabilities list */}
            <div className="space-y-4">
              {[
              {
                title: 'CNC Precision Machining',
                desc: 'Multi-axis CNC machining for complex aerospace components with tight tolerances.',
                icon: 'CogIcon',
                color: 'accent'
              },
              {
                title: 'Sheet Metal Fabrication',
                desc: 'Precision forming, cutting, and bending of aerospace-grade sheet metal structures.',
                icon: 'RectangleStackIcon',
                color: 'orange'
              },
              {
                title: 'Tooling & Structural Assemblies',
                desc: 'Complete sub-assembly integration with full traceability and quality documentation.',
                icon: 'Square3Stack3DIcon',
                color: 'green'
              },
              {
                title: 'Reverse Engineering',
                desc: 'Legacy component reverse engineering with drawing generation from physical parts.',
                icon: 'ArrowPathIcon',
                color: 'accent'
              }].
              map((cap) =>
              <div
                key={cap.title}
                className="flex items-start space-x-4 bg-card/40 border border-border rounded-xl p-5 hover:border-accent/40 transition-all duration-300 group">
                
                  <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  cap.color === 'orange' ? 'bg-mission-orange/15' : cap.color === 'green' ? 'bg-aerospace-green/15' : 'bg-accent/15'}`
                  }>
                  
                    <Icon
                    name={cap.icon as any}
                    size={22}
                    className={cap.color === 'orange' ? 'text-mission-orange' : cap.color === 'green' ? 'text-aerospace-green' : 'text-accent'} />
                  
                  </div>
                  <div>
                    <h4 className="font-headline text-base font-bold text-foreground mb-1 group-hover:text-accent transition-colors">
                      {cap.title}
                    </h4>
                    <p className="font-body text-sm text-technical-gray">{cap.desc}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right: materials */}
            <div className="bg-gradient-to-br from-card/60 to-card/30 border border-border rounded-2xl p-8">
              <h3 className="font-headline text-xl font-bold text-foreground mb-2">
                Material Expertise
              </h3>
              <p className="font-body text-sm text-technical-gray mb-6">
                20+ material grades processed across all aerospace categories.
              </p>

              <div className="space-y-5">
                {[
                {
                  category: 'Aluminium Alloys',
                  materials: ['Al 2024', 'Al 6061', 'Al 7075'],
                  color: 'accent'
                },
                {
                  category: 'Stainless & High-Temp',
                  materials: ['SS 304', 'SS 316', 'SS 17-4PH', 'Inconel'],
                  color: 'orange'
                },
                {
                  category: 'Titanium & Specialty',
                  materials: ['Titanium Ti-6Al-4V', 'Invar', 'CFRP Inserts', 'Brass'],
                  color: 'green'
                }].
                map((group) =>
                <div key={group.category}>
                    <p
                    className={`font-cta text-xs tracking-widest mb-2 ${
                    group.color === 'orange' ? 'text-mission-orange' : group.color === 'green' ? 'text-aerospace-green' : 'text-accent'}`
                    }>
                    
                      {group.category}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.materials.map((m) =>
                    <span
                      key={m}
                      className={`px-3 py-1 rounded-lg border font-mono text-xs ${
                      group.color === 'orange' ? 'bg-mission-orange/10 border-mission-orange/20 text-mission-orange/80' :
                      group.color === 'green' ? 'bg-aerospace-green/10 border-aerospace-green/20 text-aerospace-green/80' : 'bg-accent/10 border-accent/20 text-accent/80'}`
                      }>
                      
                          {m}
                        </span>
                    )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <p className="font-cta text-xs text-technical-gray tracking-wider mb-3">DIGITAL PLATFORM</p>
                <div className="flex flex-wrap gap-2">
                  {['Real-Time Project Tracking', 'Digital Quality Reports', 'Client Portal Access', 'AI-Integrated RFQ'].map((f) =>
                  <div key={f} className="flex items-center space-x-1.5">
                      <Icon name="CheckCircleIcon" size={14} className="text-aerospace-green" />
                      <span className="font-body text-xs text-technical-gray">{f}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 7: CERTIFICATIONS ── */}
      <Slide slideId="certifications" className="bg-gradient-to-b from-[#020c14] via-[#030f1a] to-[#020c14] py-24">
        <GridBg />
        <GlowOrb className="top-1/4 left-1/4 w-[500px] h-[500px] bg-aerospace-green/5" />
        <GlowOrb className="bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent/5" />
        <ScanLine />

        {/* Animated corner brackets */}
        <div className="absolute top-24 left-8 w-8 h-8 border-t-2 border-l-2 border-aerospace-green/40 pointer-events-none" />
        <div className="absolute top-24 right-8 w-8 h-8 border-t-2 border-r-2 border-aerospace-green/40 pointer-events-none" />
        <div className="absolute bottom-8 left-8 w-8 h-8 border-b-2 border-l-2 border-accent/30 pointer-events-none" />
        <div className="absolute bottom-8 right-8 w-8 h-8 border-b-2 border-r-2 border-accent/30 pointer-events-none" />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20">
          {/* Header */}
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-px bg-aerospace-green" />
            <span className="font-cta text-xs text-aerospace-green tracking-widest">SLIDE 08 · CERTIFICATIONS & APPROVALS</span>
            <div className="flex-1 h-px bg-gradient-to-r from-aerospace-green/30 to-transparent" />
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-3">
            <div>
              <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                Globally{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-aerospace-green via-accent to-aerospace-green">
                  Certified.
                </span>{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-mission-orange">
                  Fully Approved.
                </span>
              </h2>
              <p className="font-body text-base text-technical-gray mt-2 max-w-2xl">
                Every unit across all Wings Bharath facilities is qualified and compliant with the world's most stringent aerospace, defence, and quality standards — certified by TÜV SÜD.
              </p>
            </div>
            {/* Airbus attestation image */}
            <div className="relative flex-shrink-0 cert-card-reveal" style={{ animationDelay: '0.1s' }}>
              <div className="relative w-40 h-28 lg:w-48 lg:h-32 rounded-xl overflow-hidden border border-accent/30 cert-glow-accent">
                <img
                  src="/assets/images/TH8-1783401436695.png"
                  alt="Airbus Approved Supplier attestation certificate document"
                  className="w-full h-full object-cover opacity-90" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-[#020c14]/70 via-transparent to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="font-cta text-[9px] text-accent tracking-widest bg-[#020c14]/80 px-2 py-0.5 rounded">AIRBUS ATTESTATION</span>
                </div>
                {/* shimmer */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-0 bottom-0 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent" style={{ animation: 'shimmerLine 3s ease-in-out infinite' }} />
                </div>
              </div>
            </div>
          </div>

          {/* ── ANIMATED CERTIFICATION CARDS ── */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
            {
              code: 'AS9100D',
              label: 'Aerospace Quality Management System',
              sub: 'Rev D · TÜV SÜD Certified',
              icon: 'ShieldCheckIcon',
              color: 'green',
              delay: '0.15s',
              tag: 'QMS'
            },
            {
              code: 'ISO 14001',
              label: 'Environmental Management System',
              sub: '2015 · TÜV SÜD Certified',
              icon: 'GlobeAltIcon',
              color: 'green',
              delay: '0.35s',
              tag: 'EMS'
            },
            {
              code: 'ISO 27001',
              label: 'Information Security Management',
              sub: '2022 · TÜV SÜD Certified',
              icon: 'LockClosedIcon',
              color: 'accent',
              delay: '0.55s',
              tag: 'ISMS'
            },
            {
              code: 'ISO 45001',
              label: 'Occupational Health & Safety',
              sub: '2018 · TÜV SÜD Certified',
              icon: 'HeartIcon',
              color: 'accent',
              delay: '0.75s',
              tag: 'OH&S'
            },
            {
              code: 'AIRBUS',
              label: 'Airbus Approved Supplier',
              sub: 'A320 Program · Active',
              icon: 'PaperAirplaneIcon',
              color: 'orange',
              delay: '0.95s',
              tag: 'APPROVED'
            },
            {
              code: 'DEFENCE',
              label: 'Defence Licence',
              sub: 'MoD Certified · Active',
              icon: 'StarIcon',
              color: 'orange',
              delay: '1.15s',
              tag: 'LICENSED'
            }].
            map((cert) => {
              const isGreen = cert.color === 'green';
              const isOrange = cert.color === 'orange';
              const borderColor = isGreen ? 'border-aerospace-green/35' : isOrange ? 'border-mission-orange/35' : 'border-accent/35';
              const bgColor = isGreen ? 'bg-aerospace-green/6' : isOrange ? 'bg-mission-orange/6' : 'bg-accent/6';
              const hoverBorder = isGreen ? 'hover:border-aerospace-green/70' : isOrange ? 'hover:border-mission-orange/70' : 'hover:border-accent/70';
              const iconBg = isGreen ? 'bg-aerospace-green/15' : isOrange ? 'bg-mission-orange/15' : 'bg-accent/15';
              const iconColor = isGreen ? 'text-aerospace-green' : isOrange ? 'text-mission-orange' : 'text-accent';
              const tagColor = isGreen ? 'text-aerospace-green border-aerospace-green/30 bg-aerospace-green/10' : isOrange ? 'text-mission-orange border-mission-orange/30 bg-mission-orange/10' : 'text-accent border-accent/30 bg-accent/10';
              const glowClass = isGreen ? 'cert-glow-green' : isOrange ? 'cert-glow-orange' : 'cert-glow-accent';
              return (
                <div
                  key={cert.code}
                  className={`cert-card-reveal relative flex flex-col items-center text-center p-4 rounded-2xl border ${borderColor} ${bgColor} ${hoverBorder} ${glowClass} transition-all duration-300 group cursor-default`}
                  style={{ animationDelay: cert.delay }}>
                  
                  {/* Top tag */}
                  <span className={`font-cta text-[9px] tracking-widest border rounded-full px-2 py-0.5 mb-3 ${tagColor}`}>{cert.tag}</span>
                  {/* Icon circle */}
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${iconBg} border ${borderColor}`}>
                    <Icon name={cert.icon as any} size={22} className={iconColor} />
                  </div>
                  {/* Code */}
                  <p className={`font-headline text-lg font-bold mb-1 ${iconColor}`}>{cert.code}</p>
                  {/* Label */}
                  <p className="font-body text-[11px] text-foreground/80 leading-tight mb-2">{cert.label}</p>
                  {/* Sub */}
                  <p className="font-cta text-[9px] text-technical-gray tracking-wider">{cert.sub}</p>
                  {/* Shimmer overlay */}
                  <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                    <div className={`absolute top-0 bottom-0 w-1/2 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100`} style={{ animation: 'shimmerLine 1.5s ease-in-out' }} />
                  </div>
                </div>);

            })}
          </div>

          {/* ── ALL UNITS QUALIFIED BANNER ── */}
          <div className="relative rounded-2xl overflow-hidden border border-aerospace-green/25 bg-gradient-to-r from-aerospace-green/8 via-accent/5 to-aerospace-green/8 p-5 mb-5 cert-card-reveal" style={{ animationDelay: '1.35s' }}>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-aerospace-green/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-aerospace-green/20 border border-aerospace-green/40 flex items-center justify-center cert-glow-green">
                  <Icon name="CheckBadgeIcon" size={22} className="text-aerospace-green" />
                </div>
                <div>
                  <p className="font-headline text-sm font-bold text-aerospace-green tracking-wide">ALL UNITS · FULLY QUALIFIED</p>
                  <p className="font-cta text-[10px] text-technical-gray tracking-widest">ACROSS ALL WINGS BHARATH FACILITIES</p>
                </div>
              </div>
              <div className="w-px h-10 bg-aerospace-green/20 hidden md:block" />
              <p className="font-body text-sm text-technical-gray leading-relaxed text-center md:text-left">
                Every manufacturing unit within the Wings Bharath group is independently qualified and compliant with <span className="text-foreground font-semibold">AS9100D, ISO 14001, ISO 27001, ISO 45001</span> — all certified by <span className="text-accent font-semibold">TÜV SÜD</span> — alongside active <span className="text-mission-orange font-semibold">Airbus Approved Supplier</span> status and <span className="text-mission-orange font-semibold">Defence Licence</span>. No exceptions. No compromises.
              </p>
            </div>
          </div>

          {/* ── BOTTOM CREDIBILITY STRIP ── */}
          <div className="grid grid-cols-3 gap-3 cert-card-reveal" style={{ animationDelay: '1.55s' }}>
            {[
            { icon: 'ShieldCheckIcon', label: 'TÜV SÜD Certified', sub: 'Independent 3rd-party audit', color: 'green' },
            { icon: 'GlobeAltIcon', label: 'Globally Recognised', sub: 'IAQG · IATF · ISO standards', color: 'accent' },
            { icon: 'BuildingOffice2Icon', label: 'All Facilities Covered', sub: 'Every unit, every process', color: 'orange' }].
            map((item) => {
              const isGreen = item.color === 'green';
              const isOrange = item.color === 'orange';
              const iconColor = isGreen ? 'text-aerospace-green' : isOrange ? 'text-mission-orange' : 'text-accent';
              const iconBg = isGreen ? 'bg-aerospace-green/15' : isOrange ? 'bg-mission-orange/15' : 'bg-accent/15';
              return (
                <div key={item.label} className="flex items-center gap-3 bg-card/30 border border-border rounded-xl px-4 py-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                    <Icon name={item.icon as any} size={18} className={iconColor} />
                  </div>
                  <div>
                    <p className={`font-headline text-xs font-bold ${iconColor}`}>{item.label}</p>
                    <p className="font-cta text-[10px] text-technical-gray tracking-wider">{item.sub}</p>
                  </div>
                </div>);

            })}
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 8: STATS ── */}
      <Slide slideId="stats" className="bg-[#030f1a] py-24">
        <GridBg />
        <GlowOrb className="top-0 left-0 w-[400px] h-[400px] bg-accent/6" />
        <GlowOrb className="bottom-0 right-0 w-[400px] h-[400px] bg-mission-orange/5" />

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-7xl pt-20">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-8 h-px bg-accent" />
            <span className="font-cta text-xs text-accent tracking-widest">SLIDE 09 · BY THE NUMBERS</span>
          </div>

          <h2 className="font-headline text-4xl lg:text-5xl font-bold text-foreground mb-8">
            Performance{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-mission-orange">
              Metrics
            </span>
          </h2>

          {/* Visual image banner */}
          <div className="relative rounded-2xl overflow-hidden mb-10 border border-accent/15">
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_176b49e1c-1764654653366.png"
              alt="Hi-tech aerospace manufacturing facility with advanced machinery and precision equipment"
              className="w-full h-44 object-cover opacity-50" />
            
            <div className="absolute inset-0 bg-gradient-to-r from-[#030f1a]/90 via-[#030f1a]/40 to-[#030f1a]/90" />
            <div className="absolute inset-0 flex items-center justify-center gap-12 px-8">
              <div className="text-center">
                <p className="font-headline text-4xl font-bold text-accent">15+</p>
                <p className="font-cta text-[10px] text-technical-gray tracking-widest uppercase">Years of Excellence</p>
              </div>
              <div className="w-px h-12 bg-accent/20" />
              <div className="text-center">
                <p className="font-headline text-4xl font-bold text-mission-orange">240+</p>
                <p className="font-cta text-[10px] text-technical-gray tracking-widest uppercase">Projects Delivered</p>
              </div>
              <div className="w-px h-12 bg-accent/20" />
              <div className="text-center">
                <p className="font-headline text-4xl font-bold text-aerospace-green">99.97%</p>
                <p className="font-cta text-[10px] text-technical-gray tracking-widest uppercase">Quality Rate</p>
              </div>
              <div className="w-px h-12 bg-accent/20" />
              <div className="text-center">
                <p className="font-headline text-4xl font-bold text-accent">50+</p>
                <p className="font-cta text-[10px] text-technical-gray tracking-widest uppercase">Active Partnerships</p>
              </div>
            </div>
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-mission-orange/30 to-transparent" />
          </div>

          {/* Big number grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            <StatPill value="200+" label="COMMERCIAL PROJECTS" color="accent" />
            <StatPill value="30+" label="DEFENCE PLATFORMS" color="orange" />
            <StatPill value="10+" label="SPACE PROGRAMS" color="green" />
            <StatPill value="20+" label="MATERIAL GRADES" color="accent" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            <StatPill value="99.97%" label="QUALITY SUCCESS RATE" color="green" />
            <StatPill value="98.5%" label="ON-TIME DELIVERY" color="accent" />
            <StatPill value="15+" label="YEARS EXCELLENCE" color="orange" />
            <StatPill value="50+" label="ACTIVE PARTNERSHIPS" color="green" />
          </div>

          {/* Value props */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
            {
              title: 'Precision Engineering',
              desc: 'Every component crafted to space-grade tolerances with full dimensional traceability.',
              icon: '🎯'
            },
            {
              title: 'Mission-Critical Commitment',
              desc: 'Every timeline treated as mission-critical. Zero compromise on quality or delivery.',
              icon: '🚀'
            },
            {
              title: 'Digital Manufacturing',
              desc: 'AI-integrated platform for real-time project visibility, RFQ, and client portal access.',
              icon: '⚡'
            }].
            map((v) =>
            <div
              key={v.title}
              className="bg-gradient-to-br from-card/60 to-card/30 border border-border rounded-2xl p-6 hover:border-accent/40 transition-all duration-300">
              
                <div className="text-3xl mb-3">{v.icon}</div>
                <h4 className="font-headline text-base font-bold text-foreground mb-2">{v.title}</h4>
                <p className="font-body text-sm text-technical-gray leading-relaxed">{v.desc}</p>
              </div>
            )}
          </div>
        </div>
      </Slide>

      {/* ── SLIDE 9: CONTACT / CTA ── */}
      <Slide slideId="contact" className="bg-gradient-to-br from-[#020c14] via-[#030f1a] to-[#020c14] py-24">
        <GridBg />
        <ScanLine />
        <GlowOrb className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-accent/6" />

        {/* Rotating ring */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] pointer-events-none">
          <div className="rotate-ring absolute inset-0 rounded-full border border-accent/8" />
          <div
            className="absolute inset-12 rounded-full border border-mission-orange/6"
            style={{ animation: 'rotateRing 25s linear infinite reverse' }} />
          
        </div>

        <div className="relative z-10 mx-auto px-6 lg:px-8 max-w-4xl pt-20 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-8 h-px bg-accent" />
            <span className="font-cta text-xs text-accent tracking-widest">SLIDE 10 · ENGAGE WITH US</span>
            <div className="w-8 h-px bg-accent" />
          </div>

          <h2 className="font-headline text-4xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
            Ready to Build
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-accent via-[#66fcff] to-mission-orange">
              Mission-Critical?
            </span>
          </h2>

          <p className="font-body text-lg text-technical-gray max-w-2xl mx-auto mb-12 leading-relaxed">
            Submit your RFQ, explore our capabilities, or connect with our engineering team. Wings Bharath is your precision manufacturing partner for aerospace, defence, and space programs.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              href="/rfq"
              className="group relative inline-flex items-center space-x-3 px-8 py-4 rounded-xl font-cta text-sm font-medium overflow-hidden transition-all duration-300 hover:-translate-y-1"
              style={{
                background: 'linear-gradient(135deg, rgba(0,212,255,0.15) 0%, rgba(238,149,114,0.15) 100%)',
                border: '1px solid rgba(0,212,255,0.5)'
              }}>
              
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
              </span>
              <Icon name="DocumentTextIcon" size={18} className="text-accent" />
              <span className="text-accent tracking-widest">SUBMIT RFQ</span>
            </Link>

            <Link
              href="/capabilities"
              className="inline-flex items-center space-x-3 px-8 py-4 rounded-xl font-cta text-sm font-medium bg-card/60 border border-border text-technical-gray hover:border-accent/50 hover:text-accent transition-all duration-300 hover:-translate-y-1">
              
              <Icon name="WrenchScrewdriverIcon" size={18} />
              <span className="tracking-widest">EXPLORE CAPABILITIES</span>
            </Link>

            <Link
              href="/about"
              className="inline-flex items-center space-x-3 px-8 py-4 rounded-xl font-cta text-sm font-medium bg-card/60 border border-border text-technical-gray hover:border-aerospace-green/50 hover:text-aerospace-green transition-all duration-300 hover:-translate-y-1">
              
              <Icon name="InformationCircleIcon" size={18} />
              <span className="tracking-widest">ABOUT US</span>
            </Link>
          </div>

          {/* Footer strip */}
          <div className="border-t border-border pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/images/Screenshot_2026-06-28_at_11.22.51_AM-1782626245471.png"
                alt="Wings Bharath"
                className="w-10 h-10 object-contain" />
              
              <div className="text-left">
                <p className="font-accent text-sm font-bold text-foreground">WINGS BHARATH</p>
                <p className="font-cta text-[10px] text-accent tracking-wider">AI INTEGRATED MANUFACTURING</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Badge text="AIRBUS APPROVED" color="green" />
              <Badge text="RAFALE CERTIFIED" color="orange" />
              <Badge text="DEFENCE GRADE" color="accent" />
            </div>
            <p className="font-cta text-xs text-technical-gray">© 2026 Wings Bharath</p>
          </div>
        </div>
      </Slide>
    </main>);

}